/**
 * 权限核心服务
 * 实现菜单数据的统一加载、缓存管理和权限检查
 */

import { getPlatV1AccessMenus, getPlatV1RoleMenuItems } from '@/services/swagger/pingtaijiekou';
import { permissionUtils } from '@/utils/permissionUtils';

// 使用全局API命名空间
type AccessMenusItem = API.AccessMenusItem;

// 权限数据存储接口
export interface PermissionData {
  menus: AccessMenusItem[];
  userPermissions: string[]; // 用户拥有的菜单权限ID列表
  lastUpdated: number;
}

// 权限服务配置
export interface PermissionServiceConfig {
  cacheTimeout?: number; // 缓存超时时间（毫秒），默认30分钟
  autoRefresh?: boolean; // 是否自动刷新
  enableLocalStorage?: boolean; // 是否启用localStorage持久化
  maxRetryAttempts?: number; // 最大重试次数
  autoLoadRealPermissions?: boolean; // 是否自动加载真实权限数据，默认false
}

/**
 * 权限核心服务类
 */
export class PermissionService {
  private static instance: PermissionService;
  private permissionData: PermissionData | null = null;
  private config: Required<PermissionServiceConfig>;
  private isLoading = false;

  private constructor(config: PermissionServiceConfig = {}) {
    this.config = {
      cacheTimeout: 30 * 60 * 1000, // 30分钟
      autoRefresh: true,
      enableLocalStorage: true,
      maxRetryAttempts: 3,
      autoLoadRealPermissions: false, // 默认不自动加载真实权限
      ...config
    };
  }

  /**
   * 获取单例实例
   */
  public static getInstance(config?: PermissionServiceConfig): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService(config);
    }
    return PermissionService.instance;
  }

  /**
   * 加载菜单权限数据
   * 只在登录或刷新时调用一次
   */
  public async loadPermissionData(forceRefresh = false): Promise<PermissionData> {
    // 如果正在加载，等待完成
    if (this.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!this.isLoading) {
            resolve(this.permissionData!);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    // 检查缓存是否有效
    if (!forceRefresh && this.isCacheValid()) {
      return this.permissionData!;
    }

    this.isLoading = true;

    try {
      // 调用API获取菜单数据
      const menuResponse = await getPlatV1AccessMenus({});
      const menus = menuResponse.menus || [];

      // 初始化菜单权限工具
      permissionUtils.initUserMenus(menus);

      // 获取用户真实的权限数据
      // 注意：这里需要传入实际的用户ID，目前使用临时值
      const currentUser = localStorage.getItem('currentUser');
      let userPermissions: string[] = [];
      
      if (currentUser && this.config.autoLoadRealPermissions) {
        // 只有当启用自动加载真实权限时才调用接口
        try {
          const userData = JSON.parse(currentUser);
          const roleId = userData.roleId;
          if (roleId) {
            userPermissions = await this.extractRealUserPermissions(roleId, menus);
          } else {
            userPermissions = this.extractUserPermissions(menus);
          }
        } catch (error) {
          console.error('解析用户信息失败:', error);
          userPermissions = this.extractUserPermissions(menus);
        }
      } else {
        // 如果没有用户信息或未启用自动加载，使用临时权限数据
        userPermissions = this.extractUserPermissions(menus);
      }

      this.permissionData = {
        menus,
        userPermissions,
        lastUpdated: Date.now()
      };

      return this.permissionData;
    } catch (error) {
      console.error('加载权限数据失败:', error);
      throw new Error('权限数据加载失败');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * 从菜单数据中提取用户权限（临时实现）
   * 实际应该从用户角色或专门的权限接口获取
   */
  private extractUserPermissions(menus: AccessMenusItem[]): string[] {
    const permissions: string[] = [];
    
    const traverse = (menuItems: AccessMenusItem[]) => {
      menuItems.forEach(menu => {
        if (menu.menuId && menu.enabled === '1') {
          permissions.push(menu.menuId);
        }
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children);
        }
      });
    };
    
    traverse(menus);
    return permissions;
  }

  /**
   * 获取用户真实的权限数据
   * 通过调用角色菜单关系接口获取用户实际拥有的权限
   */
  private async extractRealUserPermissions(roleId: string, menus: AccessMenusItem[]): Promise<string[]> {
    try {
      // 调用角色菜单关系接口获取用户权限
      // 通过roleId获取角色对应的菜单权限
      const roleMenuResponse = await getPlatV1RoleMenuItems({
        by: 'role',
        dataId: roleId // 使用roleId而不是userId
      });
      
      if (roleMenuResponse?.items) {
        // 从返回的数据中提取菜单ID列表
        return roleMenuResponse.items
          .filter(item => item.scope) // 确保有权限范围
          .map(item => item.menuId)
          .filter(Boolean) as string[];
      }
      
      // 如果没有获取到权限数据，回退到临时实现
      console.warn('无法获取用户真实权限，使用临时权限数据');
      return this.extractUserPermissions(menus);
      
    } catch (error) {
      console.error('获取用户真实权限失败:', error);
      // 出错时回退到临时实现
      return this.extractUserPermissions(menus);
    }
  }

  /**
   * 生成权限码
   * 基于菜单的menuComp字段或其他唯一标识生成权限码
   */
  public generatePermissionCode(menu: AccessMenusItem): string {
    // 优先使用menuComp字段作为权限码
    if (menu.menuComp) {
      return menu.menuComp;
    }
    
    // 如果没有menuComp，基于menuId和其他属性生成
    if (menu.menuId) {
      return `perm:${menu.menuId}`;
    }
    
    // 最后的备选方案
    return `perm:${menu.menuPath || 'unknown'}`;
  }

  /**
   * 解析权限码
   * 将权限码解析为结构化的权限信息
   */
  public parsePermissionCode(permissionCode: string): {
    type: 'menu' | 'page' | 'function' | 'custom';
    resourceId: string;
    action?: string;
    isValid: boolean;
  } {
    if (!permissionCode) {
      return { type: 'custom', resourceId: '', isValid: false };
    }
    
    // 解析不同格式的权限码
    if (permissionCode.startsWith('menu:')) {
      return {
        type: 'menu',
        resourceId: permissionCode.substring(5),
        isValid: true
      };
    } else if (permissionCode.startsWith('page:')) {
      return {
        type: 'page',
        resourceId: permissionCode.substring(5),
        isValid: true
      };
    } else if (permissionCode.startsWith('function:')) {
      const parts = permissionCode.substring(9).split(':');
      return {
        type: 'function',
        resourceId: parts[0] || '',
        action: parts[1] || '',
        isValid: parts.length >= 1
      };
    } else if (permissionCode.startsWith('perm:')) {
      return {
        type: 'custom',
        resourceId: permissionCode.substring(5),
        isValid: true
      };
    }
    
    // 默认情况下认为是自定义权限码
    return {
      type: 'custom',
      resourceId: permissionCode,
      isValid: true
    };
  }

  /**
   * 批量生成权限码
   */
  public generatePermissionCodes(menus: AccessMenusItem[]): Map<string, string> {
    const permissionMap = new Map<string, string>();
    
    const traverse = (menuItems: AccessMenusItem[]) => {
      menuItems.forEach(menu => {
        if (menu.menuId) {
          const code = this.generatePermissionCode(menu);
          permissionMap.set(menu.menuId, code);
        }
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children);
        }
      });
    };
    
    traverse(menus);
    return permissionMap;
  }

  /**
   * 验证权限码格式
   */
  public validatePermissionCode(code: string): boolean {
    if (!code) return false;
    
    // 支持的格式：menu:xxx, page:xxx, function:xxx:action, perm:xxx, 或自定义格式
    const validPatterns = [
      /^menu:[\w\-]+$/,           // menu:menuId
      /^page:[\w\-]+$/,            // page:menuId
      /^function:[\w\-]+:[\w\-]+$/, // function:menuId:action
      /^perm:[\w\-\.\/]+$/,       // perm:anything
      /^[\w\-\.\/]+$/             // 自定义格式
    ];
    
    return validPatterns.some(pattern => pattern.test(code));
  }

  /**
   * 延迟函数
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 保存数据到localStorage
   */
  private saveToLocalStorage(data: PermissionData): void {
    try {
      const serializedData = JSON.stringify({
        menus: data.menus,
        userPermissions: data.userPermissions,
        lastUpdated: data.lastUpdated,
        version: '1.0'
      });
      localStorage.setItem('permission_cache', serializedData);
    } catch (error) {
      console.error('保存权限缓存失败:', error);
    }
  }

  /**
   * 从localStorage加载数据
   */
  private loadFromLocalStorage(): PermissionData | null {
    try {
      const cached = localStorage.getItem('permission_cache');
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      
      // 验证数据版本和完整性
      if (parsed.version !== '1.0' || !parsed.menus || !parsed.userPermissions) {
        localStorage.removeItem('permission_cache');
        return null;
      }
      
      return {
        menus: parsed.menus,
        userPermissions: parsed.userPermissions,
        lastUpdated: parsed.lastUpdated
      };
    } catch (error) {
      console.error('加载权限缓存失败:', error);
      localStorage.removeItem('permission_cache');
      return null;
    }
  }

  /**
   * 检查缓存数据是否仍然有效
   */
  private isCacheStillValid(cachedData: PermissionData): boolean {
    const now = Date.now();
    return (now - cachedData.lastUpdated) < this.config.cacheTimeout!;
  }

  /**
   * 检查缓存是否有效
   */
  private isCacheValid(): boolean {
    if (!this.permissionData) return false;
    
    const now = Date.now();
    return (now - this.permissionData.lastUpdated) < this.config.cacheTimeout!;
  }

  /**
   * 检查用户是否对指定菜单有权限
   */
  public checkMenuPermission(menuId: string): boolean {
    if (!this.permissionData) {
      console.warn('权限数据未加载，请先调用 loadPermissionData');
      return false;
    }

    const result = {
      hasPermission: permissionUtils.hasMenuIdPermission(menuId)
    };

    return result.hasPermission;
  }

  /**
   * 检查用户是否对指定路径有权限
   */
  public checkPathPermission(menuPath: string): boolean {
    if (!this.permissionData) {
      console.warn('权限数据未加载，请先调用 loadPermissionData');
      return false;
    }

    const result = permissionUtils.canAccessPage(menuPath);

    return result.hasPermission;
  }

  /**
   * 获取用户有权限访问的菜单树
   */
  public getAccessibleMenus(): AccessMenusItem[] {
    if (!this.permissionData) {
      console.warn('权限数据未加载，请先调用 loadPermissionData');
      return [];
    }

    return permissionUtils.getAccessibleMenuTree();
  }

  /**
   * 获取原始菜单数据
   */
  public getRawMenus(): AccessMenusItem[] {
    if (!this.permissionData) {
      console.warn('权限数据未加载，请先调用 loadPermissionData');
      return [];
    }

    return this.permissionData.menus;
  }

  /**
   * 获取用户权限列表
   * 如果权限数据未加载，会自动尝试加载
   */
  public async getUserPermissions(): Promise<string[]> {
    // 如果权限数据未加载，尝试自动加载
    if (!this.permissionData) {
      console.warn('权限数据未加载，正在自动加载...');
      try {
        await this.loadPermissionData();
      } catch (error) {
        console.error('自动加载权限数据失败:', error);
        return [];
      }
    }
      
    return this.permissionData!.userPermissions;
  }
    
  /**
   * 同步获取用户权限列表（不推荐，仅用于兼容旧代码）
   */
  public getUserPermissionsSync(): string[] {
    if (!this.permissionData) {
      console.warn('权限数据未加载，请先调用 loadPermissionData 或使用异步版本 getUserPermissions()');
      return [];
    }
      
    return this.permissionData.userPermissions;
  }

  /**
   * 清除缓存
   */
  public clearCache(): void {
    this.permissionData = null;
  }

  /**
   * 强制刷新权限数据
   */
  public async refresh(): Promise<PermissionData> {
    return this.loadPermissionData(true);
  }

  /**
   * 获取权限数据状态
   */
  public getStatus(): {
    isLoaded: boolean;
    isLoading: boolean;
    lastUpdated: number | null;
    cacheValid: boolean;
  } {
    return {
      isLoaded: !!this.permissionData,
      isLoading: this.isLoading,
      lastUpdated: this.permissionData?.lastUpdated || null,
      cacheValid: this.isCacheValid()
    };
  }
}

// 导出单例实例
export const permissionService = PermissionService.getInstance();

// 便捷的工具函数
export const loadPermissions = async (forceRefresh = false): Promise<PermissionData> => {
  return permissionService.loadPermissionData(forceRefresh);
};

export const hasMenuPermission = (menuId: string): boolean => {
  return permissionService.checkMenuPermission(menuId);
};

export const hasPathPermission = (menuPath: string): boolean => {
  return permissionService.checkPathPermission(menuPath);
};

export const getAccessibleMenuTree = (): AccessMenusItem[] => {
  return permissionService.getAccessibleMenus();
};

export const getRawMenuData = (): AccessMenusItem[] => {
  return permissionService.getRawMenus();
};